import '../../logs_inspector_table/index.js';
import type {
  ImmutableProgram,
  ImmutableResult,
  InitialProperties,
  UpdateHandler,
  Cmd,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type {FetchLogsHandler} from '../../../base/logs_inspector_table/architecture.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import type {Nullable} from '@gl/lit/types/general.js';
import type LogsInspectorModal from './index.js';

export interface LogsInspectorHandlers {
  fetchLogs?: FetchLogsHandler;
}

export type Model = {
  modal: {
    open: boolean;
    label: string;
  };
  header?: Nullable<TemplateResult<1>>;
  handlers?: LogsInspectorHandlers;
};

export const Msg = makeVariants({
  closeModal: Variant<void>()('close-modal'),
  setLabel: Variant<string>()('set-label'),
  setOpen: Variant<boolean>()('set-open'),
  setHeader: Variant<Nullable<TemplateResult<1>>>()('set-header'),
  setHandlers: Variant<LogsInspectorHandlers>()('set-handlers'),
});

export type Msg = VariantsFor<typeof Msg>;
// -- Init

function init(props: InitialProperties): Readonly<Model> {
  const label = <string>props.get('label') ?? 'Logs Inspector';
  const open = <boolean>props.get('open') ?? false;
  return {
    modal: {
      label,
      open,
    },
  };
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'close-modal':
      return [
        {modal: {...model.modal, open: false}},
        cmd((host: LogsInspectorModal) =>
          host.dispatchEvent(new Event('closed'))
        ),
      ];
    case 'set-label':
      return {
        ...model,
        modal: {...model.modal, label: msg.value},
      } as const;
    case 'set-open':
      return {
        ...model,
        modal: {...model.modal, open: msg.value},
      } as const;
    case 'set-header':
      return {
        ...model,
        header: msg.value,
      };
    case 'set-handlers':
      return {
        ...model,
        handlers: msg.value,
      } as const;
  }
}

function view(model: Model, handleUpdate: UpdateHandler<Model, Msg>) {
  return html` <fly-modal
    ?open=${model.modal.open}
    label=${ifDefined(model.modal.label)}
    @closed=${() => handleUpdate(Msg.closeModal())}
  >
    <div slot="header-aside"><fly-icon icon="closed"></fly-icon></div>
    <div class="logs-inspector-modal_content">
      <div class="logs-inspector-modal__content__header">
        ${model.header ? html`${model.header}` : html`<slot></slot>`}
      </div>

      <div class="logs-inspector-modal__content__table">
        <logs-inspector-table
          .fetchLogsHandler=${model.handlers?.fetchLogs}
        ></logs-inspector-table>
      </div>
    </div>
  </fly-modal>`;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
